import React from "react";
import { Card, Row, Col, Typography, Button, Badge, Space, Progress } from "antd";
import Box from "../../../components/Box/Box";
import FormSetup from "../../../components/Forms/FormSetup";
import { MasterConfigs } from "../../../interfaces/entity";
import dayjs from "dayjs";
import { FormInstance } from "antd/lib";

const { Title, Text } = Typography;

interface LeaveStatsCardProps {
  totalLeavesBalance: number;
  totalLeavesConsumed: number;
  totalHolidaysBalance: number;
  pendingRequests: number;
}

const LeaveStatsCard: React.FC<LeaveStatsCardProps> = ({
  totalLeavesBalance,
  totalLeavesConsumed,
  totalHolidaysBalance,
  pendingRequests,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [form, setForm] = React.useState<FormInstance>(null);

  const totalLeaves = totalLeavesBalance + totalLeavesConsumed;
  const leavesConsumedPercent = (totalLeavesConsumed / totalLeaves) * 100;
  const leavesRemainingPercent = (totalLeavesBalance / totalLeaves) * 100;

  const config: MasterConfigs = {
    api: () => "/leaves/user/apply",
    modalTitle: () => "Apply Leave",
    getFormColumns: () => [
      {
        type: "date",
        label: "Start Date",
        name: "startDate",
        rules: [{ required: true, message: "Please select a start date" }],
        dateProps: {
          disabledDate: (current) => {
            return current && current < dayjs().subtract(1, 'days') || current && current > dayjs().add(1, 'year') || current && current > form?.getFieldValue('endDate');
          },
        }
      },
      {
        type: "date",
        label: "End Date",
        name: "endDate",
        rules: [{ required: true, message: "Please select an end date" }],
        dateProps: {
          disabledDate: (current) => {
            return current && current < dayjs().subtract(1, 'days') || current && current > dayjs().add(1, 'year') || current && current < form?.getFieldValue('startDate');
          },
        }
      },
      { span: 8 },
      {
        type: "select",
        label: "Leave Type",
        name: "leaveType",
        isCombo: true,
        rules: [{ required: true, message: "Please select a leave type" }],
      },
      { span: 16 },
      {
        type: "textarea",
        label: "Reason",
        name: "reason",
        span: 16,
        rules: [{ required: true, message: "Please provide a reason" }],
      },
    ],
    combo: [
      {
        name: "leaveType",
        api: "/leaves/user/leaves/list",
        idKey: "_id",
        render: (e) => `${e?.leaveType?.leaveName} - (${e?.available})`,
      }
    ]
  }

  return (
    <Box
      title={'My Leave Stats'}
      actions={[
        <Button type="primary" onClick={() => setIsOpen(true)}>Apply Leave</Button>,
      ]}
    >
      <FormSetup config={config} open={isOpen} isModal={true} onClose={() => setIsOpen(false)} form={setForm} />
      <Text> Working hard yeah? Request time off on the right.</Text>
      <Row justify="center" gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={8} style={{ textAlign: "center" }}>
          <Space direction="vertical">
            <Text>Total Leaves Balance</Text>
            <Title level={4} style={{ color: "#722ed1", margin: 0 }}>
              {totalLeavesBalance} Days
            </Title>
            <Progress
              percent={leavesRemainingPercent}
              strokeColor="#722ed1"
              type="circle"
              format={(percent) => `${Math.round(percent)}%`}
              width={80}
            />
          </Space>
        </Col>

        <Col span={8} style={{ textAlign: "center" }}>
          <Space direction="vertical">
            <Text>Total Leaves Consumed</Text>
            <Title level={4} style={{ color: "#f5222d", margin: 0 }}>
              {totalLeavesConsumed} Days
            </Title>
            <Progress
              percent={leavesConsumedPercent}
              strokeColor="#f5222d"
              type="circle"
              format={(percent) => `${Math.round(percent)}%`}
              width={80}
            />
          </Space>
        </Col>

        <Col span={8} style={{ textAlign: "center" }}>
          <Space direction="vertical">
            <Text>Total Holidays Balance</Text>
            <Title level={4} style={{ color: "#1890ff", margin: 0 }}>
              {totalHolidaysBalance} Days
            </Title>
            <Progress
              percent={(totalHolidaysBalance / 10) * 100}
              strokeColor="#1890ff"
              type="circle"
              format={(percent) => `${Math.round(percent)}%`}
              width={80}
            />
          </Space>
        </Col>
      </Row>
    </Box>
  );
};

export default LeaveStatsCard;
